// Recent-activity feed: top 3 most-recent events by default, with a
// "View all" toggle that expands to the full filtered list (internally
// scrolled). Each row shows category dot, abbreviation, year, location,
// cases + deaths, and a confidence badge. Click selects on the map.
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EventRecord, Category } from '../lib/events';
import { pickI18n } from '../lib/events';
import { CATEGORY_COLOR, CATEGORY_LABEL, CONFIDENCE_CLASSES } from '../lib/palette';
import { totalCases } from '../lib/events';
import { formatCounts } from '../lib/counts';
import { config } from '../lib/config';

const RECENT_TOP_N = 3;

interface Props {
  events: EventRecord[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Inline mode: drop the floating-card chrome and fill the parent flex
   *  column. Used inside the persistent left sidebar. */
  inline?: boolean;
}

export function EventList({ events, selectedId, onSelect, inline }: Props) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Sorted by occurrence (most recent first). Single ordering — sort UI removed.
  const sorted = useMemo(
    () => [...events].sort((a, b) => b.start_date.localeCompare(a.start_date)),
    [events],
  );

  if (inline) {
    const visible = showAll ? sorted : sorted.slice(0, RECENT_TOP_N);
    const overflow = sorted.length - RECENT_TOP_N;
    return (
      <div className="flex flex-col">
        <div className="px-4 pb-2">
          <h3 className="text-[12.5px] font-semibold tracking-tight text-[var(--color-text)]">
            Recent activity
          </h3>
        </div>
        {sorted.length === 0 ? (
          <div className="px-4 py-4 text-[12.5px] text-[var(--color-text-3)]">
            No events match the current filters.
          </div>
        ) : (
          <>
            <div
              className={showAll ? 'overflow-y-auto' : ''}
              style={showAll ? { maxHeight: 'min(calc(100vh - 460px), 360px)' } : undefined}
            >
              <ul>
                {visible.map(o => (
                  <Row
                    key={o.id}
                    event={o}
                    selected={o.id === selectedId}
                    onClick={() => onSelect(o.id)}
                  />
                ))}
              </ul>
            </div>
            {overflow > 0 && (
              <div className="border-t border-[var(--color-line)] px-4 py-2">
                <button
                  type="button"
                  onClick={() => setShowAll(s => !s)}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-md py-1 text-[12px] font-medium text-[var(--color-text-2)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]"
                >
                  {showAll
                    ? 'Show less'
                    : <>View all <span className="mono tabular-nums">{sorted.length}</span></>}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Floating-card mode (legacy) — collapsible header + full list, no sort
  return (
    <div className="surface-glass pointer-events-auto flex w-[300px] flex-col overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex h-9 items-center gap-1.5 border-b border-[var(--color-line)] px-3 text-left transition-colors hover:bg-[var(--color-surface-2)]"
      >
        {open ? (
          <ChevronDown size={14} strokeWidth={2} className="text-[var(--color-text-3)]" />
        ) : (
          <ChevronRight size={14} strokeWidth={2} className="text-[var(--color-text-3)]" />
        )}
        <span className="text-[12px] font-semibold text-[var(--color-text)]">
          Recent activity
        </span>
      </button>

      {open && (
        <div className="max-h-[min(60vh,460px)] overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="px-3 py-4 text-[12.5px] text-[var(--color-text-3)]">
              No events match the current filters.
            </div>
          ) : (
            <ul>
              {sorted.map(o => (
                <Row
                  key={o.id}
                  event={o}
                  selected={o.id === selectedId}
                  onClick={() => onSelect(o.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ event, selected, onClick }: {
  event: EventRecord;
  selected: boolean;
  onClick: () => void;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const info = CATEGORY_LABEL[event.category as Category];
  const color = CATEGORY_COLOR[event.category as Category] ?? '#a8a29e';
  const total = totalCases(event);
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={
          'group flex w-full flex-col gap-0.5 border-b border-[var(--color-line)] px-4 py-2.5 text-left transition-colors last:border-b-0 ' +
          (selected
            ? 'bg-[var(--color-accent-soft)]'
            : 'hover:bg-[var(--color-surface-2)]')
        }
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ background: color, boxShadow: '0 0 0 2px white, 0 0 0 3px rgba(0,0,0,0.06)' }}
          />
          <span className="mono text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-text-2)]">
            {info.abbreviation}
          </span>
          <span className="mono text-[10.5px] text-[var(--color-text-3)]">
            {event.start_date.slice(0, 4)}
          </span>
          {event.status === 'active' && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[var(--color-signal-soft)] px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-[var(--color-signal)] ring-1 ring-[var(--color-signal)]/20">
              <span className="h-1 w-1 rounded-full bg-[var(--color-signal)]" />
              live
            </span>
          )}
        </div>
        <div
          className={
            'truncate text-[13px] font-medium ' +
            (selected ? 'text-[var(--color-accent)]' : 'text-[var(--color-text)]')
          }
        >
          {pickI18n(event.location.name_i18n, event.location.name, lang)}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--color-text-3)]">
          <span className="mono tabular-nums">
            <span className="font-semibold text-[var(--color-text-2)]">{total.toLocaleString()}</span>
            {' '}cases
          </span>
          {/* Non-primary count fields with non-zero values — shown after the
              primary headline (e.g. an app may also surface a deaths or
              secondary count when the value is non-zero). */}
          {formatCounts(event.counts as unknown as Record<string, number | undefined>, config)
            .filter((c) => !config.counts.fields.find((f) => f.id === c.id)?.primary && c.value > 0)
            .map((c) => (
              <span key={c.id} className="inline-flex items-center gap-2">
                <span className="text-[var(--color-text-4)]">·</span>
                <span className="mono tabular-nums">
                  <span className="font-semibold text-[var(--color-signal)]">
                    {c.value}
                  </span>{' '}{c.label.toLowerCase()}
                </span>
              </span>
            ))}
          <span
            className={
              'ml-auto rounded-full px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide ' +
              CONFIDENCE_CLASSES[event.confidence]
            }
          >
            {event.confidence}
          </span>
        </div>
      </button>
    </li>
  );
}
